/**
 * Dynamic Sizing Examples
 * Client-side filtering + "View more". Options populated from card data attributes.
 */

const selectors = {
  filter: '[data-sizing-examples-filter]',
  grid: '[data-sizing-examples-grid]',
  card: '[data-sizing-example-card]',
  lazyImg: '[data-sizing-example-img]',
  noResults: '[data-sizing-examples-no-results]',
  viewMore: '[data-sizing-examples-view-more]',
  viewMoreBtn: '[data-sizing-examples-view-more-btn]',
  viewingNum: '[data-sizing-viewing-num]',
  viewingTotal: '[data-sizing-viewing-total]',
};

const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
const DEFAULT_VISIBLE = 12;

function attrToCamel(attr) {
  return attr.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

function sortBySizeOrder(a, b) {
  const idx = s => {
    const i = SIZE_ORDER.indexOf(s.toUpperCase());
    return i === -1 ? SIZE_ORDER.length : i;
  };
  return idx(a) - idx(b) || a.localeCompare(b);
}

export default class DynamicSizingExamples {
  constructor(section) {
    this.el = section.el;
    this.initialVisible = section.data.initialVisible ?? DEFAULT_VISIBLE;
    this.loadMoreCount = section.data.loadMoreCount ?? DEFAULT_VISIBLE;
    this.visibleLimit = this.initialVisible;

    this.setupFilter();
    this.populateFilters();
    this.setupViewMore();
    this.applyVisibility();
  }

  cardMatchesFilters(card, filterValues, excludeAttr) {
    return Object.entries(filterValues).every(([key, value]) => {
      if (excludeAttr && attrToCamel(excludeAttr) === key) {
        return true;
      }
      if (!value) {
        return true;
      }
      return (card.dataset[key] ?? '').trim() === value;
    });
  }

  populateFilters() {
    const grid = this.el.querySelector(selectors.grid);
    const cards = [...(grid?.querySelectorAll(selectors.card) ?? [])];
    const filterValues = this.getFilterValues();

    this.el.querySelectorAll(selectors.filter).forEach(select => {
      const attr = select.dataset.filterAttr;
      if (!attr) {
        return;
      }

      const key = attrToCamel(attr);
      const values = new Set();

      cards.forEach(card => {
        if (!this.cardMatchesFilters(card, filterValues, attr)) {
          return;
        }
        const val = (card.dataset[key] ?? '').trim();
        if (val) {
          values.add(val);
        }
      });

      const sorted =
        attr === 'size'
          ? [...values].sort(sortBySizeOrder)
          : [...values].sort((a, b) => a.localeCompare(b));

      this.updateSelectOptions(select, sorted);
    });
  }

  updateSelectOptions(select, sorted) {
    const currentValue = (select.value ?? '').trim();
    while (select.options.length > 1) {
      select.remove(1);
    }

    sorted.forEach(val => {
      const option = document.createElement('option');
      option.value = val;
      option.textContent = val;
      select.appendChild(option);
    });
    select.value = sorted.includes(currentValue) ? currentValue : '';

    const isEmpty = sorted.length === 0;
    select.disabled = isEmpty;
    if (select.parentElement) {
      select.parentElement.style.opacity = isEmpty ? '0.4' : '';
    }
  }

  setupFilter() {
    this.el.querySelectorAll(selectors.filter).forEach(select => {
      select.replaceWith(select.cloneNode(true));
    });
    this.el.querySelectorAll(selectors.filter).forEach(select => {
      select.addEventListener('change', () => {
        this.visibleLimit = this.initialVisible;
        this.populateFilters();
        this.applyVisibility();
      });
    });
  }

  getFilterValues() {
    const values = {};
    this.el.querySelectorAll(selectors.filter).forEach(select => {
      const attr = select.dataset.filterAttr;
      if (attr) {
        values[attrToCamel(attr)] = (select.value ?? '').trim();
      }
    });
    return values;
  }

  loadCardImages(card) {
    card.querySelectorAll(selectors.lazyImg).forEach(img => {
      const fullSrc = img.dataset.src;
      if (!fullSrc) {
        return;
      }

      if (img.src.includes('placeholder') && img.dataset.srcTiny) {
        img.src = img.dataset.srcTiny;
        delete img.dataset.srcTiny;
      }
      const preload = new Image();
      preload.onload = () => {
        img.src = fullSrc;
        img.classList.remove('blur-sm');
        delete img.dataset.src;
      };
      preload.src = fullSrc;
    });
  }

  setupViewMore() {
    const btn = this.el.querySelector(selectors.viewMoreBtn);
    btn?.addEventListener('click', () => {
      this.visibleLimit += this.loadMoreCount;
      this.applyVisibility();
    });
  }

  applyVisibility() {
    const grid = this.el.querySelector(selectors.grid);
    if (!grid) {
      return;
    }

    const filterValues = this.getFilterValues();
    const hasActiveFilters = Object.values(filterValues).some(Boolean);
    const cards = grid.querySelectorAll(selectors.card);
    let matchIndex = 0;
    let totalMatching = 0;

    cards.forEach(card => {
      const matches = this.cardMatchesFilters(card, filterValues);

      if (matches) {
        totalMatching++;
        matchIndex++;
        const visible = matchIndex <= this.visibleLimit;
        card.classList.toggle('hidden', !visible);
        if (visible) {
          card.removeAttribute('data-hidden-by');
          this.loadCardImages(card);
        } else {
          card.setAttribute('data-hidden-by', 'pagination');
        }
      } else {
        card.classList.add('hidden');
        card.setAttribute('data-hidden-by', 'filter');
      }
    });

    const noResults = this.el.querySelector(selectors.noResults);
    noResults?.classList.toggle(
      'hidden',
      !hasActiveFilters || totalMatching > 0,
    );

    const viewMore = this.el.querySelector(selectors.viewMore);
    viewMore?.classList.toggle('hidden', totalMatching <= this.visibleLimit);

    const viewingNum = this.el.querySelector(selectors.viewingNum);
    const viewingTotal = this.el.querySelector(selectors.viewingTotal);
    if (viewingNum) {
      viewingNum.textContent = Math.min(totalMatching, this.visibleLimit);
    }
    if (viewingTotal) {
      viewingTotal.textContent = totalMatching;
    }
  }
}
